// ./src/hooks/useEvents.ts
//
// Loads the account's events for the All Events page. Lean read-only hook: the
// server has no date-range filter, so we pull the account's active events once
// and split upcoming/past + search client-side (see EventsPage). Auto-(re)loads
// when the signed-in user changes; race-guarded so a stale response can't win.

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import { useApp } from "@/contexts";
import { EventService, Event, EventFilter, listAllPages } from "@/utils/api";
import { SortItem } from "@/utils/api/commonApiTypes";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// Newest-created first is a stable default; the page re-sorts by date per tab.
const DEFAULT_SORT: SortItem[] = [{ name: "created_at", order: "DESC" }];

export interface UseEventsReturn {
  events: Event[];
  loading: boolean;
  /**
   * ⚠ Whether the FIRST read has COME BACK — not the same as "not loading". The read is gated on
   * `userId`, which arrives ASYNCHRONOUSLY, so on the first renders nothing has been asked for yet:
   * `loading` false and the collection empty. A page deciding on that pair paints "No events yet —
   * create your first event" at somebody who has fifty. A skeleton belongs to that first read only.
   */
  isLoaded: boolean;
  error: OpResult | null;
  /** Re-reads the list; resolves when the read is back (a refresh awaits it so its progress lasts as long as the read). */
  reload: () => Promise<void>;
  /**
   * Removes one event (only an unpublished one with no money recorded — the server refuses the rest, with why). On
   * success it leaves the list here and now; the result is returned so a refusal can be shown.
   */
  remove: (id: string) => Promise<OpResult>;
  /** Whether that event's removal is in flight (its own button spins; the rest of the list stays usable). */
  isRemoving: (id: string) => boolean;
}

export const useEvents = (): UseEventsReturn => {
  const { user } = useApp();
  const userId = user?.id ?? null;

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);
  const callIdRef = useRef(0);
  const [removingIds, setRemovingIds] = useState<Set<string>>(() => new Set());

  const load = useCallback(async () => {
    const myCall = ++callIdRef.current;
    setLoading(true);
    setError(null);

    const filter: EventFilter = {}; // all of the caller's events; splitting is client-side
    // Read whole (listAllPages) — the split/search is client-side, so a cap here silently dropped the rest (ROADMAP #118).
    const result = await listAllPages(EventService.list, filter, DEFAULT_SORT);

    if (myCall !== callIdRef.current) {
      return; // a newer load superseded this one
    }
    setLoading(false);
    // ⚠ Before the branch — a read that FAILED has still come back, and leaving the flag false
    // would hold the skeleton on screen for ever instead of showing what went wrong.
    setIsLoaded(true);

    if (result.code === OP_RESULT_CODES.OK && result.data) {
      setEvents(Array.isArray(result.data) ? (result.data as Event[]) : []);
    } else {
      setError(result);
      setEvents([]);
    }
  }, []);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, isLoaded);

  useEffect(() => {
    if (userId) {
      load();
    } else {
      // ⚠ NO `setIsLoaded(true)` HERE. `userId` is null on the first renders while the session
      // resolves, so flipping the flag here would say "read, and empty" before anything had been
      // asked for — exactly the lie this flag exists to stop. A private page always gets a user.
      setEvents([]);
    }
  }, [userId, load]);

  const remove = useCallback(async (id: string): Promise<OpResult> => {
    setRemovingIds((prev) => new Set(prev).add(id));

    try {
      const result = await EventService.remove({ id });

      if (result.code === OP_RESULT_CODES.OK) {
        setEvents((prev) => prev.filter((e) => e.id !== id));
      }

      return result;
    } finally {
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);

        return next;
      });
    }
  }, []);

  const isRemoving = useCallback((id: string) => removingIds.has(id), [removingIds]);

  return { events, loading, isLoaded, error, reload: load, remove, isRemoving };
};
